import axios from 'axios';

const WAITING_INTERVAL = 50;
const UNSAFE_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

// Translation payloads must not introduce prototype keys while being merged.
export const mergeUIT = (target = {}, incoming = {}) => {
  const result = { ...target };
  Object.keys(incoming).forEach(key => {
    if (UNSAFE_KEYS.has(key)) return;
    const value = incoming[key];
    result[key] = value && typeof value === 'object' && !Array.isArray(value)
      ? mergeUIT(own(result, key) ? result[key] : {}, value)
      : value;
  });
  return result;
};

export default function createUIT() {
  // Request-local, non-serializable workers do not belong in SSR/Vuex state.
  const pending = new Map();
  return {
    namespaced: true,
    state: { uit: {}, uitStatus: {} },
    mutations: {
      UPDATE_UIT(state, { data, locale, requests }) {
        state.uit = { ...state.uit, [locale]: mergeUIT(state.uit[locale], data) };
        const status = mergeUIT({}, state.uitStatus[locale]);
        requests.forEach(([category, keys]) => {
          if (UNSAFE_KEYS.has(category)) return;
          status[category] = { ...status[category] };
          keys.forEach(key => { if (!UNSAFE_KEYS.has(key)) status[category][key] = true; });
        });
        state.uitStatus = { ...state.uitStatus, [locale]: status };
      }
    },
    actions: {
      FETCH_UIT({ state, commit }, { uitReq, component, i18n }) {
        const locale = i18n.locale;
        const requests = [];
        if (component) {
          const seen = new Set();
          const components = [component];
          while (components.length) {
            const candidate = components.shift();
            const current = candidate && (candidate.options || candidate);
            if (!current || seen.has(current)) continue;
            seen.add(current);
            if (typeof current.uit === 'function') requests.push(...current.uit());
            if (current.components) components.push(...Object.values(current.components));
          }
        } else if (Array.isArray(uitReq)) {
          requests.push(...uitReq);
        }
        const missing = requests.map(([category, keys]) => [category, keys.filter(key => {
          const status = state.uitStatus[locale] && state.uitStatus[locale][category];
          return !UNSAFE_KEYS.has(category) && !UNSAFE_KEYS.has(key) && !(status && own(status, key) && status[key]);
        })]).filter(([, keys]) => keys.length);
        if (!missing.length) return Promise.resolve(true);

        let worker = pending.get(locale);
        if (!worker) {
          worker = { requests: new Map(), timer: null };
          worker.promise = new Promise((resolve, reject) => {
            worker.resolve = resolve;
            worker.reject = reject;
          });
          pending.set(locale, worker);
        }
        missing.forEach(([category, keys]) => {
          if (!worker.requests.has(category)) worker.requests.set(category, new Set());
          keys.forEach(key => worker.requests.get(category).add(key));
        });
        clearTimeout(worker.timer);
        worker.timer = setTimeout(() => {
          pending.delete(locale);
          const batch = [...worker.requests].map(([category, keys]) => [category, [...keys].sort()]);
          axios.post('/api/uit', { q: JSON.stringify(batch), lang: locale }, {
            params: { lang: null }, timeout: 10000
          }).then(response => {
            const data = mergeUIT({}, response.data || {});
            const messages = mergeUIT(i18n.getLocaleMessage(locale), data);
            i18n.setLocaleMessage(locale, messages);
            // Failed requests never enter the cache, so the next request can retry.
            commit('UPDATE_UIT', { data, locale, requests: batch });
            worker.resolve(true);
          }).catch(error => worker.reject(error));
        }, WAITING_INTERVAL);
        return worker.promise;
      }
    }
  };
}
